// src/features/apps/legal/PrivacyPolicyStep.tsx

import { useMemo, useState } from 'react';
import { Alert, Anchor, Badge, Button, Checkbox, Group, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useHelpTokens } from '@/features/help/useHelpTokens';
import { APP_WEB_DOMAIN_LABELS } from '@/lib/constants/app';
import type { AppCfgsDto } from '@/lib/types/App';
import { PRIVACY_TEMPLATE_VERSION, PrivacyTemplate, type PrivacyInputDraft } from './PrivacyTemplate';
import { PrivacyDocPreview } from './PrivacyDocPreview';
import { useAdoptLegal, useAppLegal } from './useAppLegal';

interface PrivacyPolicyStepProps {
  app: AppCfgsDto;
  onBack?: () => void;
  /** Continue to the next step (after adopting, or "Finish later"). */
  onNext: () => void;
  nextLabel?: string;
}

const LEGAL_NAME_MAX = 120;
const ADDRESS_MAX = 300;
const EMAIL_MAX = 254;
const JURISDICTION_MAX = 120;
const COUNTRIES_MAX = 200;

/**
 * Wizard step "Privacy policy" (owner-approved generator, 2026-09-11): the
 * customer types its legal identity once, sees the rendered policy built from
 * that plus the app's live facts (names, platforms, ads, kids), and adopts it
 * by click-through. Adoption stores THIS rendered text as a new version and
 * points the store listing at the hosted page on the app's shared address —
 * the one store-registered URL that never has to change.
 */
export function PrivacyPolicyStep({ app, onBack, onNext, nextLabel = 'Next' }: PrivacyPolicyStepProps) {
  const appCfgId = app.appCfg.core.appCfgId;
  const { operatorName } = useHelpTokens();
  const legalQuery = useAppLegal(appCfgId);
  const adoptMutation = useAdoptLegal(appCfgId);
  const [draft, setDraft] = useState<PrivacyInputDraft>(() => PrivacyTemplate.defaultDraft(app));
  const [authority, setAuthority] = useState(false);

  const facts = useMemo(() => PrivacyTemplate.derivedFacts(app), [app]);
  const effectiveDate = useMemo(() => PrivacyTemplate.todayIso(), []);
  const doc = useMemo(
    () => PrivacyTemplate.render(draft, facts, effectiveDate, operatorName),
    [draft, facts, effectiveDate, operatorName],
  );

  const current = legalQuery.data?.current ?? null;
  const staleness = current ? PrivacyTemplate.staleness(current, facts) : 'current';
  const outOfDate = staleness !== 'current';
  // A policy the customer hosts (the Store Listing's Privacy Policy URL, set
  // by hand or before the generator): keeping it is a valid choice.
  const customUrl = (app.listing?.privacyPolicyUrl ?? '').trim();
  const hasCustomUrl = customUrl !== '' && (!current || customUrl !== current.privacyPolicyUrl);
  const hostedUrl = app.web
    ? `https://${app.web.core.customName}.${APP_WEB_DOMAIN_LABELS[app.web.core.sharedDomainId]}/privacy`
    : null;
  const canAdopt = authority && draft.legalName.trim().length > 0 && hostedUrl != null;

  const set = (key: keyof PrivacyInputDraft) => (value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const handleAdopt = () => {
    adoptMutation.mutate(
      {
        templateVersion: PRIVACY_TEMPLATE_VERSION,
        inputs: { ...draft, fingerprint: PrivacyTemplate.fingerprint(facts) },
        renderedDoc: doc,
        effectiveDate,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Privacy policy adopted',
            message: hostedUrl ? `Published at ${hostedUrl}` : 'Published on your app address.',
            color: 'green',
          });
          onNext();
        },
        onError: (err) => notifications.show({ title: 'Error', message: err.message, color: 'red' }),
      },
    );
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Every app store asks for a privacy policy URL. Fill in who publishes the app; the policy
        text is written for you from that and from what the app actually does. It is published on
        your app&apos;s address and the store listing points at it.
      </Text>

      {!hostedUrl && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange">
          The app needs its address first (the Domain step) — the policy is published there.
        </Alert>
      )}

      {hasCustomUrl && (
        <Alert icon={<IconAlertCircle size={16} />} color="blue">
          This app already links to a policy you host:{' '}
          <Anchor href={customUrl} target="_blank" rel="noopener noreferrer" size="sm">
            {customUrl}
          </Anchor>
          . Keep it (nothing to do — edit it on the Store Listing tab) or adopt the generated
          policy below to replace it.
        </Alert>
      )}

      {current && (
        <Alert
          icon={outOfDate ? <IconAlertCircle size={16} /> : <IconCheck size={16} />}
          color={outOfDate ? 'orange' : 'green'}
        >
          {staleness === 'template'
            ? `The policy template was updated — review and re-adopt. Your version adopted on ${current.effectiveDate} (template ${current.templateVersion}) stays published until you do.`
            : staleness === 'facts'
              ? `Your policy adopted on ${current.effectiveDate} was written for an earlier version of the app (its name, platforms, ads, or child-directed setting changed). Review the text below and adopt it again.`
              : `Adopted ${current.effectiveDate} (version ${current.templateVersion}). Adopting again publishes a new version; the earlier one stays on record.`}{' '}
          {current.privacyPolicyUrl && (
            <Anchor href={current.privacyPolicyUrl} target="_blank" rel="noopener noreferrer" size="sm">
              View published policy
            </Anchor>
          )}
        </Alert>
      )}

      <TextInput
        label="Legal name of the publisher"
        description="The organization that owns the app — as it should appear in the policy"
        required
        maxLength={LEGAL_NAME_MAX}
        value={draft.legalName}
        onChange={(e) => set('legalName')(e.currentTarget.value)}
      />
      <Textarea
        label="Mailing address"
        description="Optional, but most stores expect one"
        autosize
        minRows={2}
        maxLength={ADDRESS_MAX}
        value={draft.address}
        onChange={(e) => set('address')(e.currentTarget.value)}
      />
      <TextInput
        label="Privacy contact email"
        description="Where viewers send questions and deletion requests"
        maxLength={EMAIL_MAX}
        value={draft.contactEmail}
        onChange={(e) => set('contactEmail')(e.currentTarget.value)}
      />
      <Group grow>
        <TextInput
          label="Governing law"
          description="State/province and country, e.g. Mississippi, USA"
          maxLength={JURISDICTION_MAX}
          value={draft.jurisdiction}
          onChange={(e) => set('jurisdiction')(e.currentTarget.value)}
        />
        <TextInput
          label="Countries where the app is offered"
          maxLength={COUNTRIES_MAX}
          value={draft.countries}
          onChange={(e) => set('countries')(e.currentTarget.value)}
        />
      </Group>

      <Group gap="xs">
        <Text size="xs" c="dimmed">
          Written from the app:
        </Text>
        <Badge size="xs" variant="light">
          {facts.appNames.join(', ') || 'no name yet'}
        </Badge>
        <Badge size="xs" variant="light">
          {facts.platforms.length > 0 ? facts.platforms.join(' + ') : 'no platform yet'}
        </Badge>
        <Badge size="xs" variant="light" color={facts.adsEnabled ? 'orange' : 'gray'}>
          {facts.adsEnabled ? 'ads on' : 'no ads'}
        </Badge>
        <Badge size="xs" variant="light" color={facts.kidsDirected ? 'orange' : 'gray'}>
          {facts.kidsDirected ? 'directed to children' : 'general audience'}
        </Badge>
      </Group>

      <PrivacyDocPreview doc={doc} maxHeight={320} />

      <Checkbox
        label={`I have authority to adopt this policy for ${draft.legalName.trim() || 'the publisher'}, and I have read it.`}
        checked={authority}
        onChange={(e) => setAuthority(e.currentTarget.checked)}
      />
      {hostedUrl && (
        <Text size="xs" c="dimmed">
          Published at <b>{hostedUrl}</b> — the store listing&apos;s Privacy Policy URL is set to
          this address when you adopt.
        </Text>
      )}

      <Group justify="space-between">
        {onBack ? (
          <Button variant="default" onClick={onBack}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Group gap="sm">
          <Button variant="default" onClick={onNext}>
            {current ? nextLabel : 'Finish later'}
          </Button>
          <Button onClick={handleAdopt} loading={adoptMutation.isPending} disabled={!canAdopt}>
            {current ? 'Adopt new version' : 'Adopt'}
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}
